import { gameRedisService, type ShopKind } from '../../gameRedis';
import type { AuctionHouseItem, PlayerData } from '../../../types/player';
import { autoSection, cap, fieldsSection, isSameUuid, jsonSection, streamIdToMs, type SourceDefinition } from '../helpers';
import { compactUuid } from '../../../utils/dataControllerFormat';

/** Redis (through argus-backend's guarded tunnel client) — live gameplay state and per-player streams. */

const SCALAR_KEYS: (keyof PlayerData)[] = ['lastKnownName', 'nickname', 'prefix', 'lastKnownIp', 'muted', 'pvp', 'universalChatEnabled', 'gameMessagesEnabled', 'prefixEffectEnabled', 'guildChatChannel', 'voteCount'];

async function loadProfile(uuid: string): Promise<{ data: PlayerData; key: string } | null> {
  for (const id of [uuid, compactUuid(uuid)]) {
    try {
      const result = await gameRedisService.getPlayerData(id);
      if (result.data && typeof result.data === 'object') return result;
    } catch (e) {
      // JSON.GET on a missing key returns null, which the service surfaces as a parse error; try the other form.
      if (!/JSON|null|404/i.test(e instanceof Error ? e.message : String(e))) throw e;
    }
  }
  return null;
}

export const redisProfileSource: SourceDefinition = {
  id: 'redis.profile',
  title: 'Player profile',
  system: 'Redis · playcdu:playerData',
  category: 'gameplay',
  phase: 1,
  description: 'The live network profile shared by every server: last known name and IP, nickname and colours, chat settings and ignored players, permissions and their use history, manual and active ranks, homes, tags, per-cluster balances and first-join dates.',
  run: async (ctx) => {
    const result = await loadProfile(ctx.uuid);
    if (!result) return { sections: [], recordCount: 0 };
    const p = result.data;
    ctx.addIps([p.lastKnownIp]);
    ctx.addAlias(p.lastKnownName);
    ctx.addAlias(p.nickname);
    if (!ctx.subject.username && p.lastKnownName) ctx.subject.username = p.lastKnownName;

    const scalars: Record<string, unknown> = {};
    for (const k of SCALAR_KEYS) scalars[k] = p[k];
    scalars.chatChannel = p.chatChannel ? `${p.chatChannel.displayName ?? ''} (${p.chatChannel.channelId ?? ''}${p.chatChannel.subId ? `/${p.chatChannel.subId}` : ''})` : null;
    scalars.ignoredPlayers = p.ignoredPlayers;
    scalars.nicknameColors = p.nicknameColors;
    scalars.manualRanks = p.manualRanks;
    scalars.permissions = p.permissions;

    const homes = Object.entries(p.homes ?? {}).flatMap(([server, list]) => Object.entries(list ?? {}).map(([home, location]) => ({ server, home, location })));
    const ranks = Object.entries(p.activeRanksCache ?? {}).map(([cluster, list]) => ({ cluster, ranks: list }));
    const tags = Object.entries(p.tags ?? {}).map(([category, list]) => ({ category, tags: list }));
    const balances = Object.entries(p.balancePerCluster ?? {}).map(([cluster, balance]) => ({ cluster, balance }));
    const firstJoins = Object.entries(p.firstJoinTable ?? {}).map(([server, at]) => ({ server, first_join: at }));

    const sections = [
      fieldsSection('Profile', scalars, { formats: { lastKnownIp: 'mono', muted: 'bool', pvp: 'bool', universalChatEnabled: 'bool', gameMessagesEnabled: 'bool', prefixEffectEnabled: 'bool', voteCount: 'number' }, labels: { lastKnownIp: 'Last known IP', lastKnownName: 'Last known name' } }),
      autoSection('Homes', homes, { as: 'table', formats: { location: 'mono' } }),
      autoSection('Balances per cluster', balances, { as: 'table', formats: { balance: 'number' } }),
      autoSection('First join per server', firstJoins, { as: 'table', formats: { first_join: 'datetime' } }),
      autoSection('Active ranks per cluster', ranks, { as: 'table' }),
      autoSection('Tags', tags, { as: 'table' }),
    ];
    if (Array.isArray(p.permissionUseHistory) && p.permissionUseHistory.length) sections.push(jsonSection('Permission use history', p.permissionUseHistory));
    sections.push(jsonSection('Raw profile document', p, `Verbatim copy of ${result.key}.`));
    return { sections, recordCount: 1 };
  },
};

export const redisItemSpawnsSource: SourceDefinition = {
  id: 'redis.item-spawns',
  title: 'Item spawn history',
  system: 'Redis · playcdu:playerItemSpawnData',
  category: 'gameplay',
  phase: 2,
  description: 'Items the player spawned through the network shop/spawn commands, per day and cluster, with whitelist state.',
  run: async (ctx) => {
    const { data } = await gameRedisService.getItemSpawnData(ctx.uuid);
    const rows = Object.entries(data?.history ?? {}).flatMap(([bucket, entries]) => (entries ?? []).map((e) => ({ bucket, ...e })));
    const c = cap(rows);
    return { sections: rows.length ? [autoSection('Spawns', c.rows, { as: 'table', total: c.total, formats: { isWhitelisted: 'bool', count: 'number' } })] : [], recordCount: rows.length };
  },
};

export const redisEffectSpawnsSource: SourceDefinition = {
  id: 'redis.effect-spawns',
  title: 'Effect spawn history',
  system: 'Redis · playcdu:playerEffectSpawnData',
  category: 'gameplay',
  phase: 2,
  description: 'Potion effects the player purchased/spawned, per day and cluster, with duration, amplifier and cost.',
  run: async (ctx) => {
    const { data } = await gameRedisService.getEffectSpawnData(ctx.uuid);
    const rows = Object.entries(data?.history ?? {}).flatMap(([bucket, entries]) => (entries ?? []).map((e) => ({ bucket, ...e })));
    const c = cap(rows);
    return { sections: rows.length ? [autoSection('Effects', c.rows, { as: 'table', total: c.total, formats: { seconds: 'duration-s', cost: 'number', amp: 'number' } })] : [], recordCount: rows.length };
  },
};

export const redisCreativeLogSource: SourceDefinition = {
  id: 'redis.creative-log',
  title: 'Creative mode log',
  system: 'Redis · playcdu:playerCreativeLog',
  category: 'logs',
  phase: 2,
  description: 'Creative-mode actions recorded against the player, with the server they happened on.',
  run: async (ctx) => {
    const { data } = await gameRedisService.getCreativeLog(ctx.uuid);
    const rows = (data?.XRANGE ?? []).map((e) => ({ time: streamIdToMs(e.id), stream_id: e.id, fqdn: e.msg?.fqdn, message: e.msg?.message }));
    const c = cap(rows);
    return { sections: rows.length ? [autoSection('Entries', c.rows, { as: 'table', total: c.total, formats: { time: 'datetime-ms', stream_id: 'mono' }, omit: ['stream_id'] })] : [], recordCount: rows.length };
  },
};

export const redisEconomyLogSource: SourceDefinition = {
  id: 'redis.economy-log',
  title: 'Economy transaction log',
  system: 'Redis · playcdu:playerEconomyLog',
  category: 'economy',
  phase: 2,
  description: 'Every economy event on the player\'s account: shop purchases and sales, auction listings, money sent and received (with the counterparty), and staff balance changes.',
  run: async (ctx) => {
    const { data } = await gameRedisService.getPlayerEconomyLog(ctx.uuid);
    const rows = (data?.XRANGE ?? []).map((e) => ({ time: streamIdToMs(e.id), ...e.msg }));
    const c = cap(rows);
    return {
      sections: rows.length ? [autoSection('Transactions', c.rows, { as: 'table', total: c.total, first: ['time', 'action', 'clusterId', 'money', 'itemDisplayName', 'count', 'from', 'to'], omit: ['user', 'item'], formats: { time: 'datetime-ms', from: 'mono', to: 'mono' } })] : [],
      recordCount: rows.length,
    };
  },
};

export const redisWarpsSource: SourceDefinition = {
  id: 'redis.warps',
  title: 'Warps owned',
  system: 'Redis · playcdu:WarpData',
  category: 'gameplay',
  phase: 2,
  description: 'Player warps the player owns, with server and position.',
  run: async (ctx) => {
    const { data } = await gameRedisService.getWarpData();
    const rows = (data?.warps ?? []).filter((w) => isSameUuid(w.owner, ctx.uuid)).map((w) => ({ fqdn: w.fqdn, name: w.name, position: w.pos }));
    return { sections: rows.length ? [autoSection('Warps', rows, { as: 'table', formats: { position: 'mono' } })] : [], recordCount: rows.length };
  },
};

export const redisSessionLogSource: SourceDefinition = {
  id: 'redis.session-log',
  title: 'Player session log',
  system: 'Redis · playcdu:playerStatLog (per cluster)',
  category: 'logs',
  phase: 2,
  description: 'Connection/session records from every cluster\'s player stream: username, IP address and playtime at the time of each entry, plus any other fields the server attached.',
  run: async (ctx) => {
    // One request: the backend pages every cluster's stream side by side on one Redis connection
    // and returns only this player's entries.
    const { clusters } = await gameRedisService.getPlayerSessions(ctx.uuid);
    if (clusters.length === 0) return { sections: [], recordCount: 0, notes: ['No clusters were discoverable from Redis, so no session streams were read.'] };
    const rows: Record<string, unknown>[] = [];
    const notes: string[] = [];
    for (const { cluster, entries, truncated, error } of clusters) {
      if (error) {
        notes.push(`Cluster ${cluster}: stream could not be read (${error}).`);
        continue;
      }
      if (truncated) notes.push(`Cluster ${cluster}: the stream is longer than the 100,000-entry read cap, so its oldest entries were not scanned.`);
      for (const e of entries) {
        if (!isSameUuid(e.msg?.uuid, ctx.uuid)) continue;
        const { uuid, ...rest } = e.msg;
        ctx.addIps([rest.ip]);
        ctx.addAlias(rest.username);
        rows.push({ time: streamIdToMs(e.id), cluster, uuid, ...rest });
      }
    }
    rows.sort((a, b) => Number(b.time ?? 0) - Number(a.time ?? 0));
    const c = cap(rows);
    return {
      sections: rows.length ? [autoSection('Sessions', c.rows, { as: 'table', total: c.total, first: ['time', 'cluster', 'username', 'ip', 'playtime'], omit: ['uuid'], formats: { time: 'datetime-ms', ip: 'mono', playtime: 'text' } })] : [],
      recordCount: rows.length,
      notes: notes.length ? notes : undefined,
    };
  },
};

const SHOP_NAMES: Record<ShopKind, string> = { AH: 'Auction house', AS: 'Admin shop', CS: 'Chest shop' };

const shopRow = (cluster: string, shop: string, item: AuctionHouseItem, role: 'seller' | 'buyer') => ({
  cluster,
  shop,
  role,
  listing_id: item.id,
  item: item.itemDisplayName ?? item.itemStack?.id,
  count: item.count,
  price: item.price,
  enabled: item.enabled,
  added: item.added,
  expires: item.expires,
  sale_date: item.saleDate,
  counterparty: role === 'seller' ? item.buyerName ?? item.buyer : item.sellerName ?? item.seller,
});

export const redisShopListingsSource: SourceDefinition = {
  id: 'redis.shop-listings',
  title: 'Shop & auction listings',
  system: 'Redis · playcdu:shopData (per cluster)',
  category: 'economy',
  phase: 2,
  description: 'Auction house, admin shop and chest shop listings on every cluster where the player is the seller or the buyer.',
  run: async (ctx) => {
    // One request: the backend reads every cluster's three shop documents in pipelined batches on
    // one Redis connection and returns only the listings where this player is seller or buyer.
    const { clusters, listings, errors } = await gameRedisService.getPlayerListings(ctx.uuid);
    if (clusters.length === 0) return { sections: [], recordCount: 0, notes: ['No clusters were discoverable from Redis, so no shop data was read.'] };
    const rows: Record<string, unknown>[] = [];
    const notes: string[] = [];
    for (const { cluster, kind, item } of listings) {
      if (isSameUuid(item.seller, ctx.uuid)) rows.push(shopRow(cluster, SHOP_NAMES[kind], item, 'seller'));
      if (item.buyer && isSameUuid(item.buyer, ctx.uuid)) rows.push(shopRow(cluster, SHOP_NAMES[kind], item, 'buyer'));
    }
    for (const { cluster, kind, error } of errors) notes.push(`${cluster} ${SHOP_NAMES[kind]}: could not be read (${error}).`);
    const c = cap(rows);
    return {
      sections: rows.length ? [autoSection('Listings', c.rows, { as: 'records', total: c.total, formats: { added: 'datetime', expires: 'datetime', sale_date: 'datetime', enabled: 'bool', price: 'number', count: 'number' } })] : [],
      recordCount: rows.length,
      notes: notes.length ? notes : undefined,
    };
  },
};
